import React, { useState } from "react";
import {
  Popover,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  Badge,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Divider
} from "@mui/material";
import { KeyboardArrowDown, Close } from "@mui/icons-material";
import { YES_NO_OPTIONS } from "../../constants/patientOptions";

const OPTION_FALLBACKS = {
  YesNo: YES_NO_OPTIONS,
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const FilterDropdownGroup = ({ title, fields, filters, onChange, studiesOptions }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "filter-popover" : undefined;

  // Count active filters in this group
  const activeCount = fields.filter(f => {
    const val = filters[f.key];
    if (Array.isArray(val) && val.length > 0) return true;
    if (typeof val === "string" && val.trim() !== "") return true;
    return false;
  }).length;

  const handleTextChange = (key, value) => {
    onChange(key, value);
  };

  const handleMultiSelectChange = (key, event) => {
    const {
      target: { value },
    } = event;
    onChange(key, typeof value === 'string' ? value.split(',') : value);
  };

  const handleClearGroup = () => {
    fields.forEach(f => onChange(f.key, undefined));
    if (title === "Medical and Family History") {
      onChange("medicalHistoryValuesOperator", undefined);
      onChange("familyHistoryValuesOperator", undefined);
      onChange("medicalFamilyOperator", undefined);
    }
    handleClose();
  };

  let popoverWidth = 320;
  if (title === "Medical and Family History") popoverWidth = 750;
  else if (title === "Personal Information") popoverWidth = 260;
  else if (title === "Lifestyle") popoverWidth = 140;

  const resolveOptions = (field) => {
    if (field.key === "irbStudies") {
      return studiesOptions;
    }

    if (field.options && field.options.length > 0) {
      return field.options;
    }

    if (field.optionsKey) {
      return OPTION_FALLBACKS[field.optionsKey] || [];
    }

    return [];
  };

  const renderControl = (field, overrideSx) => {
    const value = filters[field.key];
    const sxProp = overrideSx || { mb: 2 };
    
    if (field.control === "text") {
      return (
        <TextField
          key={field.key}
          fullWidth
          label={field.label}
          placeholder={field.placeholder}
          variant="outlined"
          size="small"
          value={value || ""}
          onChange={(e) => handleTextChange(field.key, e.target.value)}
          sx={sxProp}
        />
      );
    }
    
    if (field.control === "multiSelect") {
      const options = resolveOptions(field);
      
      const selectedValue = value || [];

      return (
        <FormControl fullWidth size="small" sx={sxProp} key={field.key}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            multiple
            value={selectedValue}
            onChange={(e) => {
              const val = e.target.value;
              if (val.includes("")) {
                onChange(field.key, []);
              } else {
                handleMultiSelectChange(field.key, e);
              }
            }}
            input={<OutlinedInput label={field.label} />}
            renderValue={(selected) => {
              return (
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selected.map(val => {
                    const opt = options.find(o => o.value === val);
                    return opt ? opt.label : val;
                  }).join(", ")}
                </Box>
              );
            }}
            MenuProps={MenuProps}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={selectedValue.indexOf(opt.value) > -1} />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    if (field.control === "select") {
      const options = resolveOptions(field);
      const selectedValue = value || "";

      return (
        <FormControl fullWidth size="small" sx={sxProp} key={field.key}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={selectedValue}
            onChange={(e) => onChange(field.key, e.target.value)}
            input={<OutlinedInput label={field.label} />}
          >
            {!field.hideNoneOption && <MenuItem value=""><em>None</em></MenuItem>}
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    if (field.control === "toggle") {
      let options = field.options || [];
      const selectedValue = value || field.defaultValue || "";
      
      return (
        <FormControl fullWidth size="small" sx={sxProp} key={field.key}>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
            {field.label}
          </Typography>
          <ToggleButtonGroup
            value={selectedValue}
            exclusive
            onChange={(e, newVal) => {
              if (newVal !== null) {
                onChange(field.key, newVal);
              }
            }}
            size="small"
            fullWidth
          >
            {options.map(opt => (
              <ToggleButton key={opt.value} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </FormControl>
      );
    }
    
    return null;
  };

  const renderMedicalGroup = () => {
    const adStatusField = fields.find(f => f.key === "alzheimersStatus");
    const medHistField = fields.find(f => f.key === "medicalHistory");
    const medOtherField = fields.find(f => f.key === "medicalHistoryOther");
    const famHistField = fields.find(f => f.key === "familyHistory");
    const famOtherField = fields.find(f => f.key === "familyHistoryOther");

    const renderToggle = (key, label, defaultValue = "ANY", opt1 = "ANY", opt2 = "ALL", width = 110) => {
      const toggle = (
        <ToggleButtonGroup
          value={filters[key] || defaultValue}
          exclusive
          onChange={(e, newVal) => {
            if (newVal !== null) onChange(key, newVal);
          }}
          size="small"
          sx={{
            minWidth: width,
            width: width,
            height: 40,
            border: label ? '1px solid rgba(0, 0, 0, 0.23)' : '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 1,
            backgroundColor: label ? 'transparent' : 'rgba(0,0,0,0.02)',
            boxSizing: 'border-box',
            '& .MuiToggleButtonGroup-grouped': {
              border: 0,
              padding: '0 8px',
              '&:not(:first-of-type)': {
                borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
              },
            }
          }}
        >
          <ToggleButton value={opt1} sx={{ textTransform: 'none', flex: 1, height: '100%' }}>{opt1}</ToggleButton>
          <ToggleButton value={opt2} sx={{ textTransform: 'none', flex: 1, height: '100%' }}>{opt2}</ToggleButton>
        </ToggleButtonGroup>
      );

      if (label) {
        return (
          <FormControl size="small" variant="outlined" sx={{ minWidth: width, width: width }}>
            <InputLabel shrink sx={{ backgroundColor: 'white', px: 0.5, mt: -0.5 }}>{label}</InputLabel>
            {toggle}
          </FormControl>
        );
      }
      return toggle;
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Alzheimer's Status Row */}
        {adStatusField && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: 260 }}>
              {renderControl(adStatusField, { mb: 0 })}
            </Box>
          </Box>
        )}

        {/* Medical History Row */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          <Box sx={{ width: 80, flexShrink: 0 }} /> {/* Placeholder for alignment */}
          {renderToggle("medicalHistoryValuesOperator", null, "ANY", "ANY", "ALL", 110)}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {medHistField && renderControl(medHistField, { mb: 0 })}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {medOtherField && renderControl(medOtherField, { mb: 0 })}
          </Box>
        </Box>

        {/* Family History Row */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          {renderToggle("medicalFamilyOperator", null, "AND", "AND", "OR", 80)}
          {renderToggle("familyHistoryValuesOperator", null, "ANY", "ANY", "ALL", 110)}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {famHistField && renderControl(famHistField, { mb: 0 })}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {famOtherField && renderControl(famOtherField, { mb: 0 })}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Badge badgeContent={activeCount} color="primary" sx={{ '& .MuiBadge-badge': { right: 0, top: 0 } }}>
        <Button
          aria-describedby={id}
          variant={activeCount > 0 ? "contained" : "outlined"}
          onClick={handleClick}
          endIcon={<KeyboardArrowDown />}
          sx={{ 
            borderColor: "#500000", 
            color: activeCount > 0 ? "#fff" : "#500000",
            backgroundColor: activeCount > 0 ? "#500000" : "transparent",
            '&:hover': {
               backgroundColor: activeCount > 0 ? "#7a0000" : "rgba(80, 0, 0, 0.04)",
               borderColor: "#7a0000",
               color: activeCount > 0 ? "#fff" : "#500000"
            },
            textTransform: "none",
            height: 40
          }}
        >
          {title}
        </Button>
      </Badge>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{ sx: { width: popoverWidth, p: 2, mt: 1 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
          <IconButton size="small" onClick={handleClose}><Close fontSize="small"/></IconButton>
        </Box>
        <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1, overflowX: 'hidden' }}>
          {title === "Medical and Family History" ? renderMedicalGroup() : fields.map(renderControl)}
        </Box>
        {activeCount > 0 && (
          <Button fullWidth variant="text" color="error" onClick={handleClearGroup} sx={{ mt: 1 }}>
            Clear Group
          </Button>
        )}
      </Popover>
    </>
  );
};

export default FilterDropdownGroup;
